import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { RotateCcw } from 'lucide-react';
import { usePostSourceTableSync } from '@/api/control/control-plane/control-plane';
import { SyncRequestSyncMode } from '@/api/control/missionImpactAPI.schemas';
import { SyncConfirmationDialog } from '@/components/profiles/SyncConfirmationDialog';
import { toast } from 'sonner';
import { useError } from '@/contexts/ErrorContext';
import type { ServiceErrorCode } from '@/contexts/ErrorContext';
import type { RunMetadataDto } from '@/api/control/missionImpactAPI.schemas';

interface ErrorDetailsDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Handler for dialog open/close state changes */
  onOpenChange: (open: boolean) => void;
  /** The run metadata containing error details */
  error: RunMetadataDto | null;
  /** Organization ID - required for reset & sync functionality */
  orgId: string;
  /** Optional callback when reset & sync is completed */
  onResetComplete?: () => void;
}

/**
 * Determines if an error code represents an irrecoverable failure
 * that requires a reset & sync to fix
 */
function isIrrecoverableError(errorCode: ServiceErrorCode | undefined): boolean {
  if (!errorCode) return false;

  // These errors are irrecoverable without a reset & sync
  const irrecoverableErrors: ServiceErrorCode[] = [
    'AzureSqlSchemaMigrationFailed', // Invalid schema migration
  ];

  return irrecoverableErrors.includes(errorCode);
}

/**
 * Reusable dialog component for displaying run error details
 * Shows error code, external error code, and external error message
 */
export function ErrorDetailsDialog({
  open,
  onOpenChange,
  error,
  orgId,
  onResetComplete
}: ErrorDetailsDialogProps) {
  const { t } = useLanguage();
  const { handleApiError, translateErrorCode } = useError();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const { mutate: resetSourceTable, isPending: isResetting } = usePostSourceTableSync({
    mutation: {
      onSuccess: () => {
        setResetDialogOpen(false);
        onOpenChange(false);
        toast.success(t.profiles.sourceTables.successReset);
        onResetComplete?.();
      },
      onError: (error) => {
        handleApiError(error, { context: 'ErrorDetailsDialog.resetSourceTable' });
      },
    },
  });

  const handleClose = () => {
    onOpenChange(false);
  };

  /**
   * Handles triggering a reset & sync for the source table
   */
  const handleResetAndSync = () => {
    if (!error) return;

    resetSourceTable({
      orgId,
      profileId: error.profileId,
      sourceId: error.sourceId,
      sourceTableId: error.sourceTableId,
      data: { syncMode: SyncRequestSyncMode.ResetAndFull },
    });
  };

  const isIrrecoverable = isIrrecoverableError(error?.errorCode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.runs.errorDialog.title}</DialogTitle>
          <DialogDescription>
            {t.runs.errorDialog.runId} {error?.runId}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium mb-2">{t.runs.errorDialog.sourceTable}</h4>
            <p className="text-sm text-muted-foreground background-neutral p-3 rounded">
              {error?.sourceTableLogicalName ?? t.runs.errorDialog.unknown}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-2">{t.runs.errorDialog.errorMessage}</h4>
            <p className="text-sm text-muted-foreground background-neutral p-3 rounded">
              {error?.errorCode
                ? translateErrorCode(error.errorCode) ?? error.errorCode
                : '-'}
            </p>
          </div>
          {/* Platform's own human-readable description of the failure, when it adds detail
              beyond the classified error code above. Suppressed when it is identical to the
              external message below (some connectors report the same text in both). */}
          {error?.errorMessage && error.errorMessage !== error.externalErrorMessage && (
            <div>
              <h4 className="text-sm font-medium mb-2">{t.runs.errorDialog.errorDetail}</h4>
              <p className="text-sm text-muted-foreground background-neutral p-3 rounded whitespace-pre-wrap break-words">
                {error.errorMessage}
              </p>
            </div>
          )}
          {/* Underlying error reported by the source/target system itself
              (Salesforce, BC, Dataverse, SQL) — the real cause an operator needs. */}
          {error?.externalErrorCode && (
            <div>
              <h4 className="text-sm font-medium mb-2">
                {t.runs.errorDialog.externalErrorCode}
              </h4>
              <p className="text-sm text-muted-foreground background-neutral p-3 rounded break-words">
                {error.externalErrorCode}
              </p>
            </div>
          )}
          {error?.externalErrorMessage && (
            <div>
              <h4 className="text-sm font-medium mb-2">
                {t.runs.errorDialog.externalErrorMessage}
              </h4>
              <p className="text-sm text-muted-foreground background-neutral p-3 rounded whitespace-pre-wrap break-words">
                {error.externalErrorMessage}
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-between">
          {isIrrecoverable ? (
            <>
              <Button
                variant="destructive"
                onClick={() => { setResetDialogOpen(true); }}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {t.profiles.sourceTables.resetAndResync}
              </Button>
              <Button variant="outline" onClick={handleClose}>
                {t.runs.errorDialog.close}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={handleClose} className="ml-auto">
              {t.runs.errorDialog.close}
            </Button>
          )}
        </div>
      </DialogContent>

      {/* Reset confirmation dialog */}
      {resetDialogOpen && (
        <SyncConfirmationDialog
          open={resetDialogOpen}
          onOpenChange={setResetDialogOpen}
          onConfirm={handleResetAndSync}
          isSyncing={isResetting}
        />
      )}
    </Dialog>
  );
}
